import { useState } from 'react';
import {
  UsersThree,
  UserList,
  Key,
  Gauge,
  Warning,
  Check,
  CopySimple,
  Eye,
  EyeClosed,
  HashStraight,
  ChatCircleDots,
  ClipboardText,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/layout/PageHeader';
import { useDashboardStats } from '@/lib/dashboard/useDashboardStats';
import { useDashboardAnalytics } from '@/lib/dashboard/useDashboardAnalytics';
import { useCompanyCode, type NeuraxCompanyCode } from '@/lib/company-code/useCompanyCode';
import { SessionsChart } from '@/components/dashboard/SessionsChart';
import { SessionTypeBreakdown } from '@/components/dashboard/SessionTypeBreakdown';
import { ActivityFeed } from '@/components/dashboard/ActivityFeed';
import { cn } from '@/lib/utils/cn';
import type { ReactNode } from 'react';

export default function DashboardPage() {
  const { data, isLoading, isError, error } = useDashboardStats();
  const { data: companyCode } = useCompanyCode();
  const analytics = useDashboardAnalytics();

  return (
    <>
      <PageHeader
        eyebrow="Overview"
        title="Welcome back"
        description="A snapshot of your team, candidates, and live NeuraX activity."
      />

      {isError && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <Warning className="mt-0.5 h-4 w-4" />
          <span>{(error as Error)?.message || 'Something went wrong loading stats.'}</span>
        </div>
      )}

      {companyCode && <CompanyCodeCard code={companyCode} />}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <KpiCard
          testId="kpi-employees"
          label="Team members"
          icon={<UsersThree className="h-4 w-4" />}
          loading={isLoading}
          value={data?.employees}
          accent="electric"
        />
        <KpiCard
          testId="kpi-end-clients"
          label="Candidates"
          icon={<UserList className="h-4 w-4" />}
          loading={isLoading}
          value={data?.endClients}
          accent="plasma"
        />
        <KpiCard
          testId="kpi-interviews"
          label="Interviews this month"
          icon={<ChatCircleDots className="h-4 w-4" />}
          loading={isLoading}
          value={data?.interviewsThisMonth}
          accent="mint"
        />
        <KpiCard
          testId="kpi-assessments"
          label="Assessments this month"
          icon={<ClipboardText className="h-4 w-4" />}
          loading={isLoading}
          value={data?.assessmentsThisMonth}
          accent="electric"
        />
        <KpiCard
          testId="kpi-licenses"
          label="Active licenses"
          icon={<Key className="h-4 w-4" />}
          loading={isLoading}
          value={data?.activeLicenses}
          accent="amber"
        />
        <QuotaCard
          loading={isLoading}
          used={data?.quota.used ?? 0}
          total={data?.quota.total ?? 0}
          percent={data?.quota.percent ?? 0}
        />
      </div>

      {/* Charts row */}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SessionsChart data={analytics.sessionsOverTime} />
        </div>
        <SessionTypeBreakdown data={analytics.sessionTypeBreakdown} />
      </div>

      {/* Activity feed */}
      <div className="mt-6">
        <ActivityFeed events={analytics.activityFeed} />
      </div>
    </>
  );
}

const ACCENTS = {
  electric: 'from-neurax-electric/20 to-transparent text-neurax-electric',
  plasma: 'from-neurax-plasma/20 to-transparent text-neurax-plasma',
  amber: 'from-neurax-amber/20 to-transparent text-neurax-amber',
  mint: 'from-neurax-mint/20 to-transparent text-neurax-mint',
} as const;

function KpiCard({
  testId,
  label,
  value,
  icon,
  loading,
  accent,
}: {
  testId: string;
  label: string;
  value?: number;
  icon: ReactNode;
  loading: boolean;
  accent: keyof typeof ACCENTS;
}) {
  return (
    <div className="glass-card relative overflow-hidden p-5">
      <div
        className={cn('absolute inset-x-0 top-0 h-px bg-gradient-to-r opacity-70', ACCENTS[accent])}
      />
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <div
          className={cn(
            'grid h-8 w-8 place-items-center rounded-lg bg-white/[0.04]',
            ACCENTS[accent].split(' ').pop()
          )}
        >
          {icon}
        </div>
      </div>
      <div className="mt-4">
        {loading ? (
          <div
            data-testid="kpi-skeleton"
            className="h-9 w-20 animate-pulse rounded-md bg-white/5"
          />
        ) : (
          <p
            data-testid={testId}
            className="font-display text-3xl font-semibold tracking-tight text-white"
          >
            {value ?? 0}
          </p>
        )}
      </div>
    </div>
  );
}

function CompanyCodeCard({ code }: { code: NeuraxCompanyCode }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(code.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }

  const usagePct = code.max_uses
    ? Math.min(100, Math.round((code.uses_count / code.max_uses) * 100))
    : 0;

  return (
    <div data-testid="company-code-card" className="glass-card relative mb-4 overflow-hidden p-5">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-neurax-amber/40 via-neurax-electric/40 to-transparent" />
      <div className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-neurax-amber/10 blur-3xl" />

      <div className="relative flex flex-wrap items-start justify-between gap-5">
        <div className="flex items-start gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-gradient-to-br from-neurax-amber/30 to-neurax-electric/30 text-neurax-amber">
            <HashStraight className="h-5 w-5" />
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-neurax-amber/80">
              Company code
            </p>
            <p className="mt-0.5 text-sm font-semibold text-white">
              Share this with your candidates
            </p>
            <p className="mt-1 max-w-md text-xs text-muted-foreground">
              They'll enter it the first time they open the NeuraX desktop app to link it to your
              team.
            </p>
          </div>
        </div>

        <div className="ml-auto flex shrink-0 flex-col items-end gap-2">
          <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/30 px-3 py-2">
            <code className="font-mono text-sm tracking-wide text-white">
              {revealed ? code.code : maskCode(code.code)}
            </code>
            <button
              type="button"
              data-testid="reveal-company-code"
              onClick={() => setRevealed((v) => !v)}
              aria-label={revealed ? 'Hide code' : 'Reveal code'}
              className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-white/5 hover:text-white"
            >
              {revealed ? <EyeClosed className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
            <button
              type="button"
              data-testid="copy-company-code"
              onClick={copy}
              aria-label="Copy code"
              className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-white/5 hover:text-white"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-neurax-mint" />
              ) : (
                <CopySimple className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
          <p data-testid="company-code-uses" className="text-[11px] text-muted-foreground">
            <span className="text-white">{code.uses_count}</span>
            <span> / </span>
            <span>{code.max_uses ?? '∞'}</span>
            <span> uses</span>
            {code.max_uses ? <span> · {usagePct}%</span> : null}
          </p>
        </div>
      </div>
    </div>
  );
}

function maskCode(c: string) {
  if (c.length <= 6) return '•'.repeat(c.length);
  return `${c.slice(0, 4)}••••${c.slice(-3)}`;
}

function QuotaCard({
  used,
  total,
  percent,
  loading,
}: {
  used: number;
  total: number;
  percent: number;
  loading: boolean;
}) {
  return (
    <div className="glass-card relative overflow-hidden p-5">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-neurax-mint/40 to-transparent" />
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Monthly quota
        </p>
        <div className="grid h-8 w-8 place-items-center rounded-lg bg-white/[0.04] text-neurax-mint">
          <Gauge className="h-4 w-4" />
        </div>
      </div>

      {loading ? (
        <div
          data-testid="kpi-skeleton"
          className="mt-4 h-9 w-32 animate-pulse rounded-md bg-white/5"
        />
      ) : (
        <>
          <div className="mt-4 flex items-baseline gap-2">
            <p
              data-testid="kpi-quota"
              className="font-display text-3xl font-semibold tracking-tight text-white"
            >
              <span>{used}</span>
              <span className="ml-1 text-base font-medium text-muted-foreground">/ {total}</span>
            </p>
          </div>
          <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-white/5">
            <div
              className="h-full rounded-full bg-gradient-to-r from-neurax-mint to-neurax-electric transition-all"
              style={{ width: `${Math.min(100, percent)}%` }}
            />
          </div>
          <p data-testid="kpi-quota-percent" className="mt-2 text-xs text-muted-foreground">
            {percent}% used this month
          </p>
        </>
      )}
    </div>
  );
}
